<script setup>
/**
 * FeatureDeepDive — two narrative blocks (KPIs normalized + offline-
 * resilient) with copy on one side and a real product screenshot on
 * the other, mirroring layout direction per block.
 *
 * v2: screenshots replace the prior HTML mockups via the same locale-
 * aware pattern used by ProductTabs (import.meta.glob + _es/_en/_el
 * suffix, fallback to _es when a locale variant is missing).
 *
 *  - KPIs normalized → team detail · Overview tab (KPI cards + activity
 *    trend + category donut). Tells the "comparable numbers" story.
 *  - Offline-resilient → team detail · Members tab (one row per
 *    employee with status, utilization, productivity and a per-member
 *    sparkline). The "no minute lost" story plays in the sparklines.
 */
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { Calculator, Globe2, ArrowRight } from "lucide-vue-next";
import AnimatedSection from "@/components/AnimatedSection.vue";
import LandingScreenshotFrame from "./LandingScreenshotFrame.vue";

const screenshotModules = import.meta.glob(
  "@/assets/landing/screens/*.png",
  { eager: true, import: "default" }
);

const SUPPORTED_LOCALES = ["es", "en", "el"];
const screenshotUrl = (slug, locale) => {
  const lc = SUPPORTED_LOCALES.includes(locale) ? locale : "es";
  const key = `/src/assets/landing/screens/${slug}_${lc}.png`;
  return (
    screenshotModules[key] ||
    screenshotModules[`/src/assets/landing/screens/${slug}_es.png`]
  );
};

const { t, locale } = useI18n();

const features = computed(() => [
  {
    icon: Calculator,
    eyebrow: t("landing.deep_dive.normalized.eyebrow"),
    title: t("landing.deep_dive.normalized.title"),
    description: t("landing.deep_dive.normalized.description"),
    bullets: [
      t("landing.deep_dive.normalized.bullets.0"),
      t("landing.deep_dive.normalized.bullets.1"),
      t("landing.deep_dive.normalized.bullets.2"),
    ],
    impact: {
      value: "0",
      unit: "% sesgo",
      label: t("landing.deep_dive.normalized.impact_label"),
    },
    slug: "normalized-kpis",
    images: [
      {
        src: screenshotUrl("team-overview", locale.value),
        alt: t("landing.deep_dive.normalized.title"),
        url: "app.fenixguardian.com/app/teams/.../overview",
      },
    ],
  },
  {
    icon: Globe2,
    eyebrow: t("landing.deep_dive.offline.eyebrow"),
    title: t("landing.deep_dive.offline.title"),
    description: t("landing.deep_dive.offline.description"),
    bullets: [
      t("landing.deep_dive.offline.bullets.0"),
      t("landing.deep_dive.offline.bullets.1"),
      t("landing.deep_dive.offline.bullets.2"),
    ],
    impact: {
      value: "100",
      unit: "% datos",
      label: t("landing.deep_dive.offline.impact_label"),
    },
    slug: "offline-resilient",
    images: [
      {
        src: screenshotUrl("team-members", locale.value),
        alt: t("landing.deep_dive.offline.title"),
        url: "app.fenixguardian.com/app/teams/.../members",
      },
    ],
  },
]);
</script>

<template>
  <section class="py-24 bg-background">
    <div class="container mx-auto px-6 space-y-24 lg:space-y-32">
      <AnimatedSection
        v-for="(feature, idx) in features"
        :key="feature.slug"
        animation="fade-up"
      >
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <!-- Copy column -->
          <div
            :class="[
              'lg:col-span-5',
              idx % 2 === 0 ? 'lg:order-1' : 'lg:order-2',
            ]"
          >
            <div class="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/20">
              <component :is="feature.icon" class="w-4 h-4 text-accent" />
              <span class="text-xs font-semibold uppercase tracking-wider text-accent">
                {{ feature.eyebrow }}
              </span>
            </div>
            <h3 class="text-3xl md:text-4xl font-bold text-foreground mb-5 tracking-tight leading-tight">
              {{ feature.title }}
            </h3>
            <p class="text-base text-muted-foreground mb-6 leading-relaxed">
              {{ feature.description }}
            </p>

            <!-- Impact stat — number + unit + short label.
                 Anchors the section visually and ties to a concrete benefit. -->
            <div class="mb-8 p-5 rounded-xl bg-accent/5 border border-accent/15">
              <div class="flex items-baseline gap-1.5 mb-1">
                <span class="text-4xl lg:text-5xl font-bold text-foreground tabular-nums tracking-tight">
                  {{ feature.impact.value }}
                </span>
                <span class="text-lg font-semibold text-accent">
                  {{ feature.impact.unit }}
                </span>
              </div>
              <p class="text-xs text-muted-foreground leading-relaxed">
                {{ feature.impact.label }}
              </p>
            </div>

            <ul class="space-y-3">
              <li
                v-for="(bullet, i) in feature.bullets"
                :key="i"
                class="flex items-start gap-3 text-sm text-foreground"
              >
                <ArrowRight class="w-4 h-4 mt-0.5 text-accent flex-shrink-0" />
                <span>{{ bullet }}</span>
              </li>
            </ul>
          </div>

          <!-- Screenshot column — real product capture inside the
               browser-style frame component shared with ProductTabs. -->
          <div
            :class="[
              'lg:col-span-7',
              idx % 2 === 0 ? 'lg:order-2' : 'lg:order-1',
            ]"
          >
            <LandingScreenshotFrame :images="feature.images" />
          </div>
        </div>
      </AnimatedSection>
    </div>
  </section>
</template>
